import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight, Search } from "lucide-react";
import api from "@/lib/api";
import Seo from "@/components/common/Seo";
import { KNOWLEDGE_CATEGORIES, fmtArticleDate } from "@/lib/knowledge";

const TINTS = ["#EAF3F0", "#F5EDE1", "#F9EAEB", "#E9EFF7", "#F4F0E1", "#F2EBF4", "#FDF6F3"];

export function ArticleCard({ a }) {
  const navigate = useNavigate();
  return (
    <button
      data-testid={`article-card-${a.slug}`}
      onClick={() => navigate(`/knowledge/${a.slug}`)}
      className="group bg-white border border-neutral-200 hover:border-neutral-400 hover:-translate-y-0.5 hover:shadow-[0_12px_30px_-14px_rgba(17,17,17,0.25)] transition-all duration-200 text-left flex flex-col overflow-hidden h-full"
    >
      {a.featured_image && <img src={a.featured_image} alt={a.title} className="w-full aspect-[3/2] object-cover" loading="lazy" />}
      <div className="p-5 flex flex-col flex-1">
        <div className="text-[11px] font-mono uppercase tracking-widest text-rust">{a.category}</div>
        <h3 className="text-lg font-medium text-ink mt-2 leading-snug group-hover:text-rust transition-colors">{a.title}</h3>
        {a.description && <p className="text-sm text-neutral-600 mt-2 leading-relaxed line-clamp-2 flex-1">{a.description}</p>}
        <div className="text-[11px] font-mono text-neutral-400 mt-4">
          {a.reading_time} min read · Updated {fmtArticleDate(a.updated_at || a.published_date)}
        </div>
      </div>
    </button>
  );
}

export default function Knowledge() {
  const [articles, setArticles] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (cat) params.set("category", cat);
      api.get(`/knowledge?${params.toString()}`).then((r) => setArticles(r.data)).finally(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, cat]);

  const filtered = !q && !cat;
  const featured = filtered ? articles[0] : null;
  const rest = featured ? articles.slice(1) : articles;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <Seo title="Knowledge — Transplant Story" description="Understand transplantation. Prepare better. Guides and educational articles for patients, donors and families." />

      <h1 className="text-4xl sm:text-5xl font-light tracking-tight text-ink">Knowledge</h1>
      <p className="text-lg sm:text-xl text-neutral-600 mt-4">Understand transplantation. Prepare better.</p>

      <div className="mt-10 max-w-xl flex items-center border border-neutral-300 bg-white px-4 py-3 focus-within:border-rust transition-colors">
        <Search className="w-4 h-4 text-neutral-400 shrink-0" />
        <input
          data-testid="knowledge-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search transplant information..."
          className="bg-transparent outline-none text-sm ml-3 flex-1 placeholder:text-neutral-400"
        />
      </div>

      {loading ? (
        <p className="text-neutral-500 mt-16">Loading…</p>
      ) : articles.length === 0 ? (
        <div className="border border-dashed border-neutral-300 p-12 text-center mt-16">
          <p className="text-neutral-600">No articles match your search yet.</p>
        </div>
      ) : (
        <>
          {featured && (
            <Link
              to={`/knowledge/${featured.slug}`}
              data-testid={`featured-article-${featured.slug}`}
              className="group grid md:grid-cols-2 gap-8 items-center mt-14 bg-white border border-neutral-200 hover:border-neutral-400 transition-colors overflow-hidden"
            >
              {featured.featured_image && (
                <img src={featured.featured_image} alt={featured.title} className="w-full h-full object-cover aspect-[3/2]" loading="lazy" />
              )}
              <div className="p-8">
                <div className="text-[11px] font-mono uppercase tracking-widest text-rust">{featured.category}</div>
                <h2 className="text-2xl sm:text-3xl font-light tracking-tight text-ink mt-3 leading-snug group-hover:text-rust transition-colors">
                  {featured.title}
                </h2>
                {featured.description && <p className="text-base text-neutral-600 mt-4 leading-relaxed">{featured.description}</p>}
                <div className="text-[11px] font-mono text-neutral-400 mt-6">
                  {featured.reading_time} min read · Updated {fmtArticleDate(featured.updated_at || featured.published_date)}
                </div>
              </div>
            </Link>
          )}

          {rest.length > 0 && (
            <>
              <h2 className="text-2xl font-normal tracking-tight text-ink mt-16 mb-6">
                {filtered ? "Latest articles" : `Results${cat ? ` · ${cat}` : ""}`}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {rest.map((a) => <ArticleCard key={a.id} a={a} />)}
              </div>
            </>
          )}
        </>
      )}

      <h2 className="text-2xl font-normal tracking-tight text-ink mt-20 mb-6">Browse by category</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {KNOWLEDGE_CATEGORIES.map((c, i) => (
          <button
            key={c}
            data-testid={`knowledge-cat-${i}`}
            onClick={() => { setQ(""); setCat(cat === c ? "" : c); window.scrollTo({ top: 0, behavior: "smooth" }); }}
            style={{ backgroundColor: TINTS[i % TINTS.length] }}
            className={`organ-card group p-5 text-left flex items-center justify-between gap-3 ${cat === c ? "outline outline-1 outline-ink" : ""}`}
          >
            <span className="text-base font-medium text-ink">{c}</span>
            <ArrowUpRight className="w-4 h-4 shrink-0 text-neutral-300 group-hover:text-rust transition-colors" />
          </button>
        ))}
      </div>

      <div className="mt-20 border border-neutral-200 bg-white p-8 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
        <div>
          <h2 className="text-2xl font-light tracking-tight text-ink">Have you lived through a transplant journey?</h2>
          <p className="text-sm text-neutral-500 mt-2">Your experience could change someone else's journey.</p>
        </div>
        <Link
          to="/share"
          data-testid="knowledge-share-cta"
          className="inline-flex items-center bg-ink text-white px-6 py-3 text-sm font-medium hover:bg-rust transition-colors shrink-0"
        >
          Share Your Journey
        </Link>
      </div>
    </div>
  );
}
